import { Component, ReactNode, ErrorInfo } from "react";
import { Typography } from "../ui/typography";
import { Button } from "../ui/button";
import { getMembersLink, isNetworkError, isDefined } from "../../lib";

interface IErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
}

interface IErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<IErrorBoundaryProps, IErrorBoundaryState> {
    constructor(props: IErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<IErrorBoundaryState> {
        return {
            hasError: true,
            error,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Error caught by Error Boundary:", error, errorInfo);
        this.setState({
            error,
            errorInfo,
        });
    }

    handleReset = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = getMembersLink();
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Detect if this is a network error
            const error = this.state.error;
            const isNetwork = isDefined(error) ? isNetworkError(error) : false;

            // Determine title and message based on error type
            const title = isNetwork ? "Network Connection Error" : "Something went wrong";
            const message = isNetwork
                ? "Network connection error. Please check your internet connection and try again."
                : "We encountered an unexpected error. Please try refreshing the page or contact tech team if the problem persists.";

            return (
                <div className="flex items-center justify-center min-h-screen w-full px-6 py-12 bg-neutral-50">
                    <div className="w-full max-w-md">
                        <div className="mb-4 text-center">
                            <Typography
                                variant="h4"
                                weight="semibold"
                                color="text-neutral-900"
                                align="center"
                            >
                                {title}
                            </Typography>
                        </div>

                        <div className="mb-6 text-center">
                            <Typography variant="span" color="text-neutral-600" align="center">
                                {message}
                            </Typography>
                        </div>

                        {/* Only show technical error details for non-network errors */}
                        {isDefined(error) && !isNetwork && (
                            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
                                <Typography variant="small" weight="medium" color="text-red-700" className="mb-2">
                                    Error Details:
                                </Typography>
                                <Typography variant="xs" color="text-neutral-600" className="font-mono break-all">
                                    {error.message ?? "Unknown error occurred"}
                                </Typography>
                            </div>
                        )}

                        <div className="flex gap-3 justify-center">
                            <Button
                                onClick={this.handleReset}
                                variant="link"
                                className="text-orange-500 hover:text-orange-600 font-medium text-base underline-offset-4"
                            >
                                Try Again
                            </Button>

                            <Button
                                onClick={this.handleGoHome}
                                variant="link"
                                className="text-orange-500 hover:text-orange-600 font-medium text-base underline-offset-4"
                            >
                                Go to Home
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

